import { ChangeDetectionStrategy, Component, computed, inject, input, signal, ViewEncapsulation } from "@angular/core";
import {
    attendanceDisplayName,
    AttendanceService,
    AttendanceState,
    attendanceState,
    DBAttendeeHistoryEntry,
} from "./attendance.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { UserprefService } from "../../../services/userpref.service";
import { S25Datefilter } from "../../s25-dateformat/s25.datefilter.service";
import { IngestionFormComponent } from "../../data-ingestion/ingestion.form.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { InferSignal } from "../../../pojo/Util";
import { S25Assert } from "../../../util/s25.assert";
import { S25Util } from "../../../util/s25-util";
import { Proto } from "../../../pojo/Proto";
import ISODateString = Proto.ISODateString;
import { SeAttendanceStore } from "./se.attendance.store";

@Component({
    standalone: true,
    selector: "s25-ng-attendance-history",
    template: `
        @if (rawHistory.resource.isLoading()) {
            <s25-ng-loading-inline-static [text]="'Loading attendance history...'" />
        } @else if (rawHistory.resource.error()) {
            Unable to load attendance history
        } @else if (!history().length) {
            No attendance history available
        } @else {
            @for (action of history(); track action) {
                <div class="action-wrapper">
                    <div class="action">
                        <div class="date">{{ action.formattedDate }}</div>
                        <div class="details">
                            <div class="description">{{ action.description }}</div>
                            <div class="occurrences">
                                @for (occurrence of action.occurrences; track occurrence) {
                                    <span class="occurrence-label">{{ occurrence.label }}</span>
                                    @if (occurrence.description) {
                                        <span class="occurrence-description">{{ occurrence.description }}</span>
                                    }
                                }
                            </div>
                            @if (action.submissionId) {
                                <s25-ng-button [type]="'link'" (clicked)="toggleForm(action)">
                                    {{ action.showForm() ? "Hide" : "Show" }} Form Submission
                                </s25-ng-button>
                            }
                        </div>
                    </div>
                    @if (action.showForm()) {
                        <s25-ng-ingestion-form [submissionId]="action.submissionId" [readOnly]="true" />
                    }
                </div>
            }
        }
    `,
    styles: `
        :host {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 1rem;
        }

        .action-wrapper {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 2;
        }

        .action {
            display: grid;
            grid-template-columns: subgrid;
            grid-column: span 2;
        }

        .date {
            color: var(--color--text-muted);
        }

        .description {
            font-weight: bold;
        }

        .occurrences {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;

            &:has(.occurrence-description) {
                display: grid;
                grid-template-columns: auto 1fr;
            }
        }

        .occurrence-label {
            background: color-mix(in srgb, var(--cn-blue) 15%, var(--color--surface-bg));
            padding: 0.1em 0.25em;
            border-radius: 3px;
            font-size: 0.8em;
        }

        .occurrence-description {
            font-size: 0.9em;
        }

        s25-ng-ingestion-form {
            grid-column: 1/-1;
        }

        @media (max-width: 768px) {
            :host {
                grid-template-columns: 1fr;
                gap: 0.75rem;
            }

            .action-wrapper {
                grid-column: span 1;
                border-bottom: 1px solid var(--color--border);
                padding-bottom: 0.75rem;
            }

            .action-wrapper:last-child {
                border-bottom: none;
                padding-bottom: 0;
            }

            .action {
                grid-column: span 1;
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
            }

            .date {
                font-size: 0.85em;
            }

            .occurrences {
                &:has(.occurrence-description) {
                    grid-template-columns: 1fr;
                }
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25LoadingInlineModule, IngestionFormComponent, S25ButtonComponent],
})
export class S25AttendanceHistoryComponent {
    readonly contactId = input.required<number>();
    readonly totalOccurrences = input.required<number>();

    readonly attendanceStore = inject(SeAttendanceStore);
    readonly attendanceService = inject(AttendanceService);

    readonly rawHistory = s25Resource({
        if: () => !!this.attendanceStore.eventId() && !!this.contactId(),
        params: () => ({
            eventId: this.attendanceStore.eventId()!,
            contactId: this.contactId(),
        }),
        loader: ({ params: { eventId, contactId }, abortSignal }) => {
            return this.attendanceService.getAttendeeHistory(eventId, contactId);
        },
    });
    readonly dateTimeFormat = s25Resource({ loader: () => UserprefService.getS25DateTimeformat() });

    readonly history = computed(() => {
        const dateTimeFormat = this.dateTimeFormat.value();
        const rawHistory = this.rawHistory.value();
        const eventOccurrences = this.totalOccurrences();
        if (!dateTimeFormat || !rawHistory || !eventOccurrences) return [];

        const actions = S25Util.array.groupBy(rawHistory, (entry) => entry.createDt);
        const actionEntries = <[ISODateString, DBAttendeeHistoryEntry[]][]>Object.entries(actions);
        const history = actionEntries
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([date, entries]) => {
                const endState = entries[0].endState; // Should be the same for all entries in an action

                let description: string;
                let occurrences: { label: string; description?: string }[];
                if (eventOccurrences) {
                    // Get previous states for all occs, if they are the same, show one description for all occs
                    // If they are different, show a description for each occ
                    const previousStates = new Set(entries.map((entry) => entry.startState));
                    if (previousStates.size === 1) {
                        description = this.getTransitionDescription(entries[0].startState, endState);
                        occurrences = entries.map((entry) => ({
                            label: S25Datefilter.transform(entry.reservation.evStartDt, dateTimeFormat),
                        }));
                    } else {
                        description = `Set status to ${attendanceDisplayName[endState]}`;
                        occurrences = entries.map((entry) => ({
                            label: S25Datefilter.transform(entry.reservation.evStartDt, dateTimeFormat),
                            description: this.getTransitionDescription(entry.startState, entry.endState),
                        }));
                    }
                } else {
                    description = this.getTransitionDescription(entries[0].startState, endState);
                    occurrences = entries.map((entry) => ({
                        label: S25Datefilter.transform(entry.reservation.evStartDt, dateTimeFormat),
                    }));
                }

                return {
                    date: date,
                    formattedDate: S25Datefilter.transform(date, dateTimeFormat),
                    description,
                    occurrences,
                    submissionId: entries[0].submissionId, // Should be the same for all entries in an action
                    showForm: signal(false),
                };
            });

        history.sort((a, b) => b.date.localeCompare(a.date));
        return history;
    });

    getTransitionDescription(start: AttendanceState, end: AttendanceState): string {
        switch (end) {
            case attendanceState.NOT_INTERESTED: {
                if (start === attendanceState.NOT_INTERESTED) return "Unknown";
                if (start === attendanceState.INTERESTED) return "No longer interested";
                if (start === attendanceState.PENDING)
                    return `No longer interested; ${this.getTransitionDescription(start, attendanceState.INTERESTED)}`;
                return `No longer interested; ${this.getTransitionDescription(start, attendanceState.INTERESTED)}`;
            }
            case attendanceState.INTERESTED: {
                if (start === attendanceState.NOT_INTERESTED) return "Marked as interested";
                if (start === attendanceState.INTERESTED) return "Unknown";
                if (start === attendanceState.PENDING) return "Registration cancelled";
                return "Unregistered";
            }
            case attendanceState.PENDING: {
                if (start === attendanceState.PENDING) return "Unknown";
                return "Registered (pending approval)";
            }
            case attendanceState.REGISTERED: {
                if (start === attendanceState.NOT_INTERESTED) return "Registered";
                if (start === attendanceState.INTERESTED) return "Registered";
                if (start === attendanceState.PENDING) return "Registration approved";
                if (start === attendanceState.REGISTERED) return "Unknown";
                return "Reset to registered";
                break;
            }
            case attendanceState.WAIT_LIST: {
                if (start === attendanceState.WAIT_LIST) return "Unknown";
                return "Waitlisted";
            }
            case attendanceState.CHECKED_IN: {
                if (start === attendanceState.CHECKED_IN) return "Unknown";
                return "Checked in";
            }
            case attendanceState.NO_SHOW: {
                if (start === attendanceState.NO_SHOW) return "Unknown";
                return "Did not show";
            }
            case attendanceState.CANCELED: {
                if (start === attendanceState.CANCELED) return "Unknown";
                return "Canceled";
            }
            case attendanceState.CHECKED_OUT: {
                if (start === attendanceState.CHECKED_OUT) return "Unknown";
                return "Checked Out";
            }
            default:
                S25Assert.never(end, `Unexpected attendance state`);
        }
    }

    toggleForm(action: InferSignal<typeof this.history>[number]) {
        action.showForm.update((val) => !val);
    }
}
